import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { ApiError } from '@/_shared';
import { apiClient, queryClient, REFERENCE_DATA_QUERY_STALE_TIME_MS } from '@/_shared';

import type {
  EmailTemplateDefaultCreateSchema,
  EmailTemplateDefaultQueryAllSchema,
  EmailTemplateDefaultQuerySchema,
  EmailTemplateDefaultUpdateSchema,
} from './schemas';

export const EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY = ['EMAIL_TEMPLATE_DEFAULTS'] as const;

export const EMAIL_TEMPLATE_DEFAULTS_STALE_TIME = REFERENCE_DATA_QUERY_STALE_TIME_MS;

export function getCachedEmailTemplateDefaults(): EmailTemplateDefaultQueryAllSchema | undefined {
  return queryClient.getQueryData(EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY);
}

export async function fetchEmailTemplateDefaults(): Promise<EmailTemplateDefaultQueryAllSchema> {
  return queryClient.fetchQuery({
    ...getEmailTemplateDefaults(),
    staleTime: EMAIL_TEMPLATE_DEFAULTS_STALE_TIME,
  });
}

export function getEmailTemplateDefaults() {
  return {
    queryKey: EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<EmailTemplateDefaultQueryAllSchema>({
        endpoint: '/api/v1/email-template-defaults',
        params: 'per_page=100&include=email_template',
      });
      return res;
    },
  };
}

export function useGetEmailTemplateDefaults() {
  return useSuspenseQuery(getEmailTemplateDefaults());
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: EMAIL_TEMPLATE_DEFAULTS_QUERY_KEY });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, EmailTemplateDefaultCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/email-template-defaults',
        payload,
      }),
  });

export const useUpdateMutation = () =>
  useMutation<
    { data: EmailTemplateDefaultQuerySchema },
    ApiError,
    { id: string; payload: EmailTemplateDefaultUpdateSchema }
  >({
    mutationFn: ({ id, payload }) =>
      apiClient.request({
        endpoint: `/api/v1/email-template-defaults/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['EMAIL_TEMPLATE_DEFAULT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/email-template-defaults/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
